import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang, categoryLabels } from "@/i18n";
import { ProductCard } from "@/components/ProductCard";
import { useSEO } from "@/lib/useSEO";
import { Search } from "lucide-react";

const CATS = ["rings", "necklaces", "earrings", "bracelets"];

export default function Shop() {
  const { category } = useParams();
  const { t, lang, tl } = useLang();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");

  useSEO({ title: `Mimi Silver — ${category ? categoryLabels[category]?.[lang] : t("shop_all")}`, description: t("hero_sub") });

  useEffect(() => {
    const params = category ? { category } : {};
    api.get("/products", { params }).then((res) => setProducts(res.data));
  }, [category]);

  const visible = useMemo(() => {
    let list = products.filter((p) => tl(p.name).toLowerCase().includes(search.toLowerCase()));
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "newest") list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else list = [...list].sort((a, b) => (b.featured === true) - (a.featured === true));
    return list;
  }, [products, search, sort, tl]);

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-12 lg:py-16 fade-up min-h-[60vh]">
      <div className="text-center mb-10">
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1F2C]">
          {category ? categoryLabels[category]?.[lang] : t("shop_all")}
        </h1>
      </div>

      <div className="flex flex-wrap justify-center gap-3 mb-8">
        <Link to="/shop" data-testid="filter-all"
          className={`text-xs tracking-luxe uppercase px-5 py-2.5 border transition-colors duration-300 ${!category ? "bg-[#1A1F2C] text-white border-[#1A1F2C]" : "border-[#EAE6E1] text-[#1A1F2C] hover:border-[#C5A880]"}`}>
          {t("shop_all")}
        </Link>
        {CATS.map((c) => (
          <Link key={c} to={`/shop/${c}`} data-testid={`filter-${c}`}
            className={`text-xs tracking-luxe uppercase px-5 py-2.5 border transition-colors duration-300 ${category === c ? "bg-[#1A1F2C] text-white border-[#1A1F2C]" : "border-[#EAE6E1] text-[#1A1F2C] hover:border-[#C5A880]"}`}>
            {categoryLabels[c][lang]}
          </Link>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center mb-10 max-w-3xl mx-auto">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="shop-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search_placeholder")}
            className="w-full bg-white border border-[#EAE6E1] pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C5A880]" />
        </div>
        <select data-testid="shop-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-white border border-[#EAE6E1] px-4 py-2.5 text-sm focus:outline-none focus:border-[#C5A880] text-[#1A1F2C]">
          <option value="featured">{t("sort_featured")}</option>
          <option value="price_asc">{t("sort_price_asc")}</option>
          <option value="price_desc">{t("sort_price_desc")}</option>
          <option value="newest">{t("sort_newest")}</option>
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
        {visible.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </div>
  );
}
